import PropTypes from 'prop-types';
import React from 'react';
import { Size } from '../../constants/components';
import SplitLayout from '../layouts/splitLayout';
import GoButton from '../components/GoButton';
import StimulusButton from '../stimulus/StimulusButton';
import TextAnswer from '../components/TextAnswer';
import AudioContainer from '../audio/audioContainer';
import ScreenErrorContainer from '../error/ScreenErrorContainer';

export default class MultiChoiceQuestionView extends React.Component {
    constructor(props) {
        super(props);
        this.playStimulus = this.playStimulus.bind(this);
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else if (this.narrationIsReadyToStart(this.props)) {
            this.props.narrateScreen(this.props.stimulusAudio);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.narrationShouldStart(prevProps)) {
            this.props.narrateScreen(this.props.stimulusAudio);
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    getStimulusArea() {
        return (
            <StimulusButton
                isDisabled={this.props.isGreyedOut}
                isPlaying={this.props.isStimulusPlaying}
                onClick={this.playStimulus}
            />
        );
    }

    getSelectedResponseCallback(index) {
        return () => {
            this.props.selectResponse(index);
        };
    }

    getResponseOption(possibleResponse, index) {
        return (
            <TextAnswer
                isTabIndexDisabled={this.props.isGreyedOut || this.props.isScreenBlocked}
                text={possibleResponse.text}
                key={possibleResponse.id}
                isSelected={index === this.props.selectedIndex}
                fontSize={this.props.fontSize}
                onAnswerClick={this.getSelectedResponseCallback(index)}
            />
        );
    }

    getResponsesArea() {
        return (
            <div>
                <div className="vertical-answer-buttons">
                    {this.props.possibleResponses.map(
                        (answer, index) => this.getResponseOption(answer, index),
                        this,
                    )}
                </div>
            </div>
        );
    }

    narrationIsReadyToStart() {
        return this.props.isDataFetched;
    }

    narrationShouldStart(prevProps) {
        const isReady = this.narrationIsReadyToStart();
        const shouldStartNarrate =
            this.props.isDataFetched !== prevProps.isDataFetched ||
            this.props.questionIndex !== prevProps.questionIndex;
        return isReady && shouldStartNarrate;
    }

    playStimulus() {
        if (this.props.stimulusAudio) {
            this.props.narrateStimulus(this.props.stimulusAudio, 0);
        }
    }

    render() {
        return (
            <SplitLayout
                class={`${this.props.pageLayout}`}
                isScreenBlocked={this.props.isScreenBlocked}
                isGreyedOut={this.props.isGreyedOut}
                isFetching={false}
                upperElement={this.getStimulusArea()}
                lowerElement={this.getResponsesArea()}
            >
                <ScreenErrorContainer onRetry={this.props.onNext} />
                {this.props.isNextButtonVisible && (
                    <GoButton onClick={this.props.onNext} isEnabled={!this.props.isScreenBlocked} />
                )}
                <AudioContainer />
            </SplitLayout>
        );
    }
}

MultiChoiceQuestionView.propTypes = {
    pageLayout: PropTypes.string.isRequired,
    narrateStimulus: PropTypes.func.isRequired,
    isStimulusPlaying: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isNextButtonVisible: PropTypes.bool,
    questionIndex: PropTypes.number,
    selectedIndex: PropTypes.number,
    fontSize: PropTypes.string,
    possibleResponses: PropTypes.array,
    stimulusAudio: PropTypes.string,
    onNext: PropTypes.func.isRequired,
    cleanup: PropTypes.func.isRequired,
    selectResponse: PropTypes.func.isRequired,
    isDataFetched: PropTypes.bool,
    fetchContent: PropTypes.func.isRequired,
    isGreyedOut: PropTypes.bool,
    narrateScreen: PropTypes.func.isRequired,

    /* These are props that are never referenced as `this.props` so they trigger lint errors */
    isReadyForNextScreen: PropTypes.bool,
};

MultiChoiceQuestionView.defaultProps = {
    isStimulusPlaying: false,
    isScreenBlocked: false,
    questionIndex: undefined,
    selectedIndex: undefined,
    fontSize: Size.Large,
    possibleResponses: [],
    isNextButtonVisible: false,
    stimulusAudio: undefined,
    isGreyedOut: false,
    isReadyForNextScreen: true,
    isDataFetched: false,
};
